import { keepPreviousData, queryOptions, useQueries, useQuery } from '@tanstack/react-query'
import { DateRange } from 'react-day-picker'

import * as apiClient from '@/lib/api'
import { useQueryResetWithRefresh } from '@/lib/hooks/query-refetch-with-reset'
import { buildQueryParamsStringWithFilters } from '@/lib/utils'
import { ApiFreeTextAnalysis } from '@/modules/free-text-analysis/types'
import { useDate } from '@/stores/date-filter'
import { FreeTextAnalysisFlag } from '@/types/enums/free-text-analysis'
import { FetchParams, FetchParamsWithoutPagination } from '@/types/search'
import { QUERY_KEY_FREE_TEXT_ANALYSIS } from '@/utils/keys'

function fetchFreeTextAnalysis(date: DateRange, fetchParams: FetchParams): Promise<ApiFreeTextAnalysis> {
  return apiClient.get(`/firm/free-text-analysis?${buildQueryParamsStringWithFilters(fetchParams, date, null)}`, true)
}

export const freeTextAnalysisQueryOptions = (
  date: DateRange,
  fetchParams: FetchParams
) =>
  queryOptions({
    queryKey: [QUERY_KEY_FREE_TEXT_ANALYSIS, date, fetchParams],
    queryFn: () => fetchFreeTextAnalysis(date, fetchParams),
    placeholderData: keepPreviousData
  })

export const useFreeTextAnalysis = (fetchParams: FetchParams) => {
  const { date } = useDate()
  const queryOptions = freeTextAnalysisQueryOptions(date, fetchParams)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data?.data ?? [],
    total: query.data?.aggregate_data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

/**
 * The list endpoint's aggregate_data.total reflects however the request was filtered, so the three
 * chip counts are fetched as separate limit:1 requests (one per flag, plus one unfiltered by flag)
 * rather than derived from whichever page/flag is currently on screen.
 */
export const useFreeTextAnalysisFlagCounts = (contextFetchParams: FetchParamsWithoutPagination) => {
  const { date } = useDate()

  const countFetchParams = (flag?: FreeTextAnalysisFlag): FetchParams => ({
    ...contextFetchParams,
    limit: 1,
    offset: 0,
    ...(flag && { flag }),
  })

  const [allQuery, complaintQuery, vulnerabilityQuery] = useQueries({
    queries: [
      freeTextAnalysisQueryOptions(date, countFetchParams()),
      freeTextAnalysisQueryOptions(date, countFetchParams(FreeTextAnalysisFlag.COMPLAINT)),
      freeTextAnalysisQueryOptions(date, countFetchParams(FreeTextAnalysisFlag.VULNERABILITY)),
    ]
  })

  return {
    total: allQuery.data?.aggregate_data?.total ?? 0,
    [FreeTextAnalysisFlag.COMPLAINT]: complaintQuery.data?.aggregate_data?.total ?? 0,
    [FreeTextAnalysisFlag.VULNERABILITY]: vulnerabilityQuery.data?.aggregate_data?.total ?? 0,
  }
}
